import { MyEarnings } from '@sp/data/bif';
import { IS_IOS } from '@sp/data/env';
import { IconUsers } from '@sp/ui/icons';
import { cls } from '@sp/util/helpers';
import { VFC } from 'react';
import { formatPercentage, formatSpentMoney } from './utils';

const IconOrangeStrokes: VFC<{ size?: number; className?: string }> = ({ className, size = 28 }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.2168 12.7013C10.2509 11.046 11.328 9.43015 12.4303 7.82031"
      stroke="#FF825C"
      strokeWidth="1.5"
      strokeMiterlimit="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.8711 16.2891C13.6018 14.9127 15.5355 13.8484 17.3884 12.6562"
      stroke="#FF825C"
      strokeWidth="1.5"
      strokeMiterlimit="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.1816 20.1751C15.0062 19.8479 16.9174 19.7096 18.7481 20.0095"
      stroke="#FF825C"
      strokeWidth="1.5"
      strokeMiterlimit="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const slideClassNames = `flex flex-col justify-between relative rounded-2.5xl p-6 w-[80vw] min-w-[80vw]`;

export const IncomeListSkeleton: VFC = () => {
  const slide = <div className={cls(slideClassNames, 'h-44 bg-light animate-pulse')}></div>;
  return (
    <div className="-mx-6 px-6 pb-6 overflow-hidden flex flex-row gap-2">
      {slide}
      {slide}
    </div>
  );
};

const SecondSlide: VFC = () => (
  <div
    className={cls(
      slideClassNames,
      "bg-[#E4E9F4] bg-no-repeat bg-cover sm:bg-contain bg-[top_right_-2rem] bg-[url('/images/creator-income-second-slide-bg.svg')]",
    )}
  >
    <div>
      <h4 className="m-0 font-semibold text-xl leading-6 max-w-[220px]">
        Earn more with our new subscription-based format!
      </h4>
      <p className="m-0 mt-1 text-xs leading-4 max-w-[220px]">
        Get predictable monthly income by creating a premium channel for your audience
      </p>
    </div>
    <div className="mt-4 flex flex-row gap-1">
      <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M16.9988 10.9193C16.0465 10.431 14.9651 10.2536 13.9067 10.4121C12.8484 10.5705 11.8663 11.0569 11.0988 11.8026C10.1625 12.7401 9.63653 14.0109 9.63653 15.3359C9.63653 16.6609 10.1625 17.9318 11.0988 18.8693L15.8155 23.6693C15.9704 23.8255 16.1547 23.9495 16.3578 24.0341C16.5609 24.1187 16.7788 24.1623 16.9988 24.1623C17.2188 24.1623 17.4367 24.1187 17.6398 24.0341C17.8429 23.9495 18.0272 23.8255 18.1821 23.6693L22.8988 18.9526C23.8351 18.0151 24.3611 16.7443 24.3611 15.4193C24.3611 14.0943 23.8351 12.8234 22.8988 11.8859C22.1409 11.1266 21.1634 10.6247 20.1047 10.4512C19.0459 10.2778 17.9594 10.4415 16.9988 10.9193ZM20.5321 16.5193L16.9988 20.0526L13.4655 16.5193C13.3093 16.3643 13.1853 16.18 13.1006 15.9769C13.016 15.7738 12.9725 15.556 12.9725 15.3359C12.9725 15.1159 13.016 14.8981 13.1006 14.695C13.1853 14.4919 13.3093 14.3075 13.4655 14.1526C13.7777 13.8422 14.2002 13.6679 14.6405 13.6679C15.0808 13.6679 15.5032 13.8422 15.8155 14.1526C15.9704 14.3088 16.1547 14.4328 16.3578 14.5174C16.5609 14.602 16.7788 14.6456 16.9988 14.6456C17.2188 14.6456 17.4367 14.602 17.6398 14.5174C17.8429 14.4328 18.0272 14.3088 18.1821 14.1526C18.4944 13.8422 18.9168 13.6679 19.3571 13.6679C19.7974 13.6679 20.2199 13.8422 20.5321 14.1526C20.6883 14.3075 20.8123 14.4919 20.897 14.695C20.9816 14.8981 21.0251 15.1159 21.0251 15.3359C21.0251 15.556 20.9816 15.7738 20.897 15.9769C20.8123 16.18 20.6883 16.3643 20.5321 16.5193ZM16.9988 0.335938C14.8101 0.335937 12.6428 0.767034 10.6207 1.60461C8.59865 2.44219 6.76133 3.66985 5.21369 5.21749C2.08808 8.3431 0.332134 12.5823 0.332134 17.0026C0.317563 20.8512 1.65012 24.5835 4.0988 27.5526L0.765467 30.8859C0.534204 31.1203 0.377544 31.4179 0.315254 31.7412C0.252963 32.0646 0.287834 32.3991 0.415467 32.7026C0.553897 33.0025 0.778307 33.2545 1.06021 33.4266C1.34212 33.5987 1.6688 33.6832 1.9988 33.6693H16.9988C21.4191 33.6693 25.6583 31.9133 28.7839 28.7877C31.9095 25.6621 33.6655 21.4229 33.6655 17.0026C33.6655 12.5823 31.9095 8.3431 28.7839 5.21749C25.6583 2.09188 21.4191 0.335938 16.9988 0.335938ZM16.9988 30.3359H6.01547L7.56547 28.7859C7.87589 28.4737 8.05012 28.0512 8.05012 27.6109C8.05012 27.1706 7.87589 26.7482 7.56547 26.4359C5.38311 24.256 4.02409 21.3868 3.71995 18.3173C3.41581 15.2477 4.18537 12.1676 5.89751 9.60181C7.60965 7.036 10.1585 5.1432 13.1097 4.24589C16.0609 3.34858 19.2319 3.50228 22.0825 4.68079C24.9331 5.85931 27.2869 7.98973 28.7429 10.7091C30.1988 13.4284 30.6669 16.5685 30.0673 19.5943C29.4678 22.6201 27.8376 25.3443 25.4547 27.303C23.0717 29.2617 20.0834 30.3335 16.9988 30.3359Z"
          fill="#181828"
        />
      </svg>
      <div className="leading-5 ml-0.5">
        Contact your manager
        <br />
        to learn more!
      </div>
    </div>
  </div>
);

const enum FirstSlideState {
  initial = 'initial',
  trial = 'trial',
  purchase = 'purchase',
}

function getFirstSlideState(earnings: MyEarnings): FirstSlideState {
  if (earnings.purchasedCount > 0) return FirstSlideState.purchase;
  if (earnings.involvedCount > 0) return FirstSlideState.trial;
  return FirstSlideState.initial;
}

const Participants: VFC<Readonly<{ count: number; color: 'grey' | 'green' }>> = ({ count, color }) => (
  <div>
    <h6 className="m-0 font-semibold text-primary text-xs leading-[18px]">
      <span className="relative">
        Participants
        {count > 0 && <IconOrangeStrokes className="absolute inline-block -right-5 -top-3" />}
      </span>
    </h6>
    <div className="mt-0.5 flex flex-row items-center">
      <span className="font-semibold text-primary text-xl leading-6">{count}</span>
      <IconUsers
        className={cls('inline-block ml-0.5', color === 'grey' ? 'fill-stroke' : 'fill-substrate')}
        size={24}
      />
    </div>
  </div>
);

const Buyers: VFC<
  Readonly<{
    count: number;
    percentage: number;
    color: 'grey' | 'green';
  }>
> = ({ count, percentage, color }) => (
  <div className="absolute top-0 left-[8.45rem]">
    <h6 className="m-0 font-semibold text-primary text-xs">
      <span className="relative">
        Paid users&nbsp;
        <span
          className={cls('ml-0.5', count > 0 ? 'text-brand' : color === 'grey' ? 'text-disabled' : 'text-substrate')}
        >
          {formatPercentage(percentage)}
        </span>
        {count > 0 && <IconOrangeStrokes className="absolute inline-block -right-5 -top-3" />}
      </span>
    </h6>
    <div className="mt-0.5 flex flex-row items-center">
      <span className="font-semibold text-primary text-xl leading-6">{count}</span>
      <IconUsers
        className={cls('inline-block ml-0.5', color === 'grey' ? 'fill-stroke' : 'fill-substrate')}
        size={24}
      />
    </div>
  </div>
);

export const IncomeList: VFC<
  Readonly<{
    earnings: MyEarnings;
    onShowDetails: VoidFunction;
  }>
> = ({ earnings, onShowDetails }) => {
  const firstSlideState = getFirstSlideState(earnings);
  const buyersPercentage = earnings.purchasedCount / earnings.involvedCount;

  return (
    <div className="-mx-6 px-6 pb-6 overflow-x-auto hide-scrollbar flex flex-row gap-2">
      {firstSlideState === FirstSlideState.initial && (
        <div
          className={cls(
            slideClassNames,
            'bg-light',
            "bg-no-repeat bg-[top_-0.5rem_right_-3rem] bg-[url('/images/creator-income-first-slide-initial-bg.svg')]",
          )}
        >
          <div>
            <h4 className="m-0 font-semibold text-xl leading-6 mr-10">
              You are at the beginning <wbr />
              of your journey
            </h4>
            <p className="m-0 mt-1 text-xs leading-4 mr-10">
              Boost your activities and get your audience&apos;s attention with your offerings
            </p>
          </div>
          <div className="mt-4 relative">
            <Participants count={0} color="grey" />
            <Buyers count={0} percentage={0} color="grey" />
          </div>
        </div>
      )}

      {firstSlideState === FirstSlideState.trial && (
        <div
          role="button"
          onClick={onShowDetails}
          className={cls(
            slideClassNames,
            'bg-brand-light',
            "bg-no-repeat bg-[top_-0.5rem_right_-3rem] bg-[url('/images/creator-income-first-slide-trial-bg.svg')]",
          )}
        >
          <div>
            <h4 className="m-0 font-semibold text-xl leading-6 mr-5">
              Take a look at how many people are interested in your content 👍
            </h4>
            <p className="m-0 mt-1 text-xs leading-4 mr-10">
              You can see your current Self+ audience&nbsp;below ⬇
              <br />
              Stay active and promote your activities
            </p>
          </div>
          <div className="mt-4 relative">
            <Participants count={earnings.involvedCount} color="green" />
            <Buyers count={0} percentage={0} color="green" />
          </div>
        </div>
      )}

      {firstSlideState === FirstSlideState.purchase && (
        <div
          role="button"
          onClick={onShowDetails}
          className={cls(
            slideClassNames,
            'bg-brand-light',
            "bg-no-repeat bg-right-top bg-[url('/images/creator-income-first-slide-purchase-bg.svg')]",
          )}
        >
          <div>
            <h4 className="m-0 font-medium text-lg leading-5 text-brand">Total earnings</h4>
            <div className="font-semibold text-[2rem] leading-[2.5rem]">{formatSpentMoney(earnings.earned)}</div>
            <p className="m-0 mt-1 text-xs leading-4 mr-5">
              That&apos;s a great set of results. Keep it up!
              <br />
              Create more activities, engage more people
            </p>
          </div>
          <div className="mt-4 relative">
            <Participants count={earnings.involvedCount} color="green" />
            <Buyers count={earnings.purchasedCount} percentage={buyersPercentage} color="green" />
          </div>
        </div>
      )}

      <SecondSlide />

      {/* Safari misses right margin/padding of the flex container */}
      {IS_IOS && <div className="p-2" />}
    </div>
  );
};
